/**
 * Panel definitions for the flex/congestion dashboard (PI-659): GOPACS congestion areas,
 * redispatch requests and clearings.
 *
 * The dashboard only arranges catalog visualizations on the 24-column grid. Titles,
 * coverage notes, default periods and filter defaults are catalog config, not panel config.
 * Panel types are registry keys, resolved by DashboardPanel through the visualization
 * registry.
 */
const REQUESTS_DATASET = 'gopacs-redispatch-requests';

export const FLEX_CONGESTION_PANEL_IDS = Object.freeze({
  map: 'gopacs-congestion-map',
  requestsPerOperator: 'gopacs-requests-per-operator',
  requestedVsTraded: 'gopacs-requested-vs-traded',
  marketVsContract: 'gopacs-market-vs-contract',
  clearedBuckets: 'gopacs-cleared-buckets-chart',
  clearedVolumes: 'gopacs-cleared-volumes-chart',
});

const IDS = FLEX_CONGESTION_PANEL_IDS;

export const flexCongestionPanels = [
  {
    id: IDS.map,
    type: 'map/congestion/netherlands',
    datasetId: 'gopacs-congestion-areas',
    visualizationId: 'gopacs-congestion-map',
    gridPos: { x: 0, y: 0, w: 24, h: 18 },
  },
  {
    id: IDS.requestsPerOperator,
    type: 'datasetChart',
    datasetId: REQUESTS_DATASET,
    visualizationId: 'gopacs-requests-per-operator',
    gridPos: { x: 0, y: 18, w: 12, h: 16 },
  },
  {
    id: IDS.requestedVsTraded,
    type: 'datasetChart',
    datasetId: REQUESTS_DATASET,
    visualizationId: 'gopacs-requested-vs-traded',
    gridPos: { x: 12, y: 18, w: 12, h: 16 },
  },
  {
    id: IDS.marketVsContract,
    type: 'table/summary',
    datasetId: REQUESTS_DATASET,
    visualizationId: 'gopacs-market-vs-contract',
    gridPos: { x: 0, y: 34, w: 24, h: 14 },
  },
  {
    id: IDS.clearedBuckets,
    type: 'datasetChart',
    datasetId: 'gopacs-cleared-buckets',
    visualizationId: 'gopacs-cleared-buckets-chart',
    gridPos: { x: 0, y: 48, w: 12, h: 14 },
  },
  {
    id: IDS.clearedVolumes,
    type: 'datasetChart',
    datasetId: 'gopacs-cleared-volumes',
    visualizationId: 'gopacs-cleared-volumes-chart',
    gridPos: { x: 12, y: 48, w: 12, h: 14 },
  },
];

/**
 * Three sync groups:
 *  - the grid operator on every panel;
 *  - the congestion direction on the panels whose data has a direction;
 *  - the date range on every panel that has a free one. The map is NOT in it: its period is
 *    locked in the catalog (its figures are rolling 12 months) but it still reports its
 *    `last_7d` default, which in a date group overrides the free panels' own defaults.
 */
export const flexCongestionSyncGroups = [
  {
    id: 'congestion-operator-sync',
    global: false,
    ids: [
      IDS.map,
      IDS.requestsPerOperator,
      IDS.requestedVsTraded,
      IDS.marketVsContract,
      IDS.clearedBuckets,
      IDS.clearedVolumes,
    ],
    syncFilters: ['organisation'],
  },
  {
    id: 'congestion-direction-sync',
    global: false,
    ids: [IDS.map, IDS.requestsPerOperator, IDS.requestedVsTraded],
    syncFilters: ['congestion_direction'],
  },
  {
    id: 'congestion-date-sync',
    global: false,
    ids: [
      IDS.requestsPerOperator,
      IDS.requestedVsTraded,
      IDS.marketVsContract,
      IDS.clearedBuckets,
      IDS.clearedVolumes,
    ],
    syncDateRange: true,
  },
];
